// TODO: isolate program > composables/smaller files before moving to composition API

<template>
  <!-- the event coming up after the one on show: its sessions in one collapsed row -->
  <EarlierToday v-if="isUpcoming" class="comingUp" :sessions="upcoming">
    <!-- the event, then its dates under it as supporting information (Seán, 2026-11-03) -->
    <span class="rowTitle">VIZBI <i>Online</i> {{ eventType }}</span>
    <span class="rowDate">{{ dateRange }}</span>
  </EarlierToday>
  <template v-else>
    <!-- a way to the program, quieter than Join Meeting: "you can press this" (DESIGN.md § 5);
         beside it, on the development and test servers only, the test mode's Run Test and Final
         State (the end everyone sees once it's over), alike and as wide; the one running, an
         action button: Stop Test, Initial State (Seán, 2026-11-03) -->
    <div class="quietButtons">
      <router-link :to="programPath" class="quietButton">View Full Program</router-link>
      <!-- this event's attendees on the year's People page, alone (ATTENDEES-SPEC.md § 7) -->
      <router-link :to="attendeesPath" class="quietButton">See Attendees</router-link>
      <template v-if="canTest">
        <button v-if="testMode === 'run'" class="quietButton actionButton" @click="stopTest">
          Stop Test
        </button>
        <button v-else class="quietButton" @click="run_test">Run Test</button>
        <button v-if="testMode === 'final'" class="quietButton actionButton" @click="stopTest">
          Initial State
        </button>
        <button v-else class="quietButton" @click="showFinalState">Final State</button>
      </template>
    </div>
    <!-- Earlier today first, where it stays all day (Seán, 2026-11-03: the layout doesn't jump);
         once every session is over, the end state below it -->
    <Transition name="earlier">
      <EarlierToday v-if="earlierToday.length" class="earlierToday" :sessions="earlierToday" />
    </Transition>
    <div id="events" ref="eventsRef" :style="{'--title-size': titleSize || undefined}">
      <!-- a session that ends folds upwards out of the list (its card, under Earlier today), the
           ones below closing the gap -->
      <TransitionGroup :css="false" @enter="expand" @leave="collapse">
        <EventItem
          v-for="(item, count) in upcoming"
          :key="getSessionKey(item)"
          :item="item"
          :show-link-at="showLinkAt"
          :count="count"
          :is-check-in="getSessionKey(item) === startStamps.checkIn"
          :is-opening="getSessionKey(item) === startStamps.opening"
          @ended="eventHasEnded" />
      </TransitionGroup>
    </div>
    <!-- the event's feedback, under its sessions, from its start (Seán, 2026-11-03: for those
         leaving early; there, not promoted), and after it the thanks and what comes next -->
    <LiveEndState
      v-if="hasStarted"
      :event-name
      :is-over
      :is-preview="testMode !== ''" />
  </template>
</template>

<script setup>
const log = (msg) => process.env.VUE_APP_DEV && console.log(`LiveEvent: ${msg}`); // eslint-disable-line
const props = defineProps({
  eventName: {type: String, default: ""},
  public: {type: Boolean, default: false},
  isUpcoming: {type: Boolean, default: false} // shown collapsed, after the event on show (Live.vue)
});
// the event's first start and last end, for Live.vue to choose the event on show
const emit = defineEmits(["range"]);
import {computed, onMounted, ref, watch} from "vue";
import {useIntervalFn} from "@vueuse/core";
import {resetLiveMeeting} from "@/composables/useLiveMeeting";
import {getSessionKey} from "@/composables/sessionKey";
import {useFitLongestLine} from "@/composables/useFitLongestLine";
import {useWindowSize} from "@vueuse/core";
import {fetchProgramSheet} from "@/composables/useProgramSheet";
import {getServerNow, startServerClock} from "@/composables/serverClock";
import EventItem from "@/components/EventItem";
import EarlierToday from "@/components/EarlierToday";
import LiveEndState from "@/components/LiveEndState";
import history from "@/assets/history.json";
import stringify from "json-stringify-safe";
import useUserSafe from "@/composables/useUserSafe";
import useRoles from "@/composables/useRoles";
import {getPreferredTimeZone} from "@/composables/selectedTimeZones";
// import Zoom from "@/components/Zoom";
const {DateTime} = require("luxon");
const year = history.latestYear;
const timeZoneConference = history.years[year];
const host = window.location.host;
const showLinkAt = ref(15); // by default, show link 15 mins prior
// the zone the times are shown in: the account's or this browser's choice (selectedTimeZones.js,
// as the program page), else the browser's own
const {user} = useUserSafe();
// the test mode (Run Test, Final State): on the development servers, and for admins and testers
// anywhere (Seán, 2026-11-03)
const {isAdmin, isTester} = useRoles();
const canTest = computed(() => {
  return /^local|^dev|^192/.test(host) || isAdmin.value || isTester.value;
});
const viewerZone = computed(() => getPreferredTimeZone(user.value));
const reSorted = ref(0); // counts re-sorting events
const programOriginal = ref([]); // for testing
const test_program = ref([]); // for testing

// one card per session (Seán, 2026-11-03): rows of the sheet with the same start and title (a
// breakout's speakers, one row each) become one, its speakers in order (names: ["Brady
// Johnston", …], name: "Brady Johnston, Samuel Pantze & Vera Williams"), their image the
// composite of theirs (EventItem.vue)
const getJoinedNames = (names) => (names.length < 2 ? names[0] || ""
  : `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`);
function getMergedSessions(sessions) {
  const byKey = new Map();
  for (const session of sessions) {
    const key = `${session.startTimeStamp?.toMillis?.()}|${session.title}`;
    const card = byKey.get(key);
    if (!card) {
      byKey.set(key, {...session, names: session.name ? [session.name] : []});
      continue;
    }
    if (session.name) card.names.push(session.name);
    card.name = getJoinedNames(card.names);
  }
  return [...byKey.values()];
}

const program = computed(() => {
  log(`program()`);
  let output = [];
  let previousEndTime = "";
  let program = programOriginal.value; // local copy of original program
  function col(heading) {
    // return the index of the column with this heading
    return program[0].indexOf(heading);
  }
  // early return if the original program hasn't been mounted yet
  if (!Object.keys(programOriginal.value)) {
    return;
  }
  if (Object.keys(programOriginal.value).length === 0) {
    return;
  }
  //log(`Found events: ${Object.keys(programOriginal.value).length}`);

  for (let i = 1; i < program.length; i++) {
    let item = {eventName: props.eventName}; // One event (its meeting: Video/Link.vue)
    log(`program item = ${program[i]}`);
    //if (!program[i][col("Mode")].includes("Online")) continue; // skip
    item.online = program[i][col("Mode")].includes("Online");
    log(`program item is online`);
    if (props.eventName.includes("Masterclass")) {
      // for the Masterclass, skip all non-Masterclass items
      if (!program[i][col("Category")].includes("Masterclass")) continue;
      log(`program item is part of Masterclass`);
    }
    if (!props.eventName.includes("Masterclass")) {
      // for VIZBI, skip all Masterclass items
      if (program[i][col("Category")].includes("Masterclass")) continue;
      log(`program item is part of VIZBI`);
    }
    item.name = program[i][col("Name")];
    item.title = program[i][col("Talk/Role/Event")];
    // if (!item.name && !item.title.match(/^meet|Q&A/i)) {
    // skip event items with no speaker or not beginning with 'meet'
    // continue;
    // }
    if (!program[i][col("Date")]) continue; // skip items with no date
    item.date = program[i][col("Date")];
    item.public = program[i][col("Public")] === "TRUE";
    item.bio = program[i][col("Bio")];
    item.description = program[i][col("Description")];
    item.stream = program[i][col("Stream")];
    item.video = program[i][col("Video")];
    if (!program[i][col("Duration/Time")]) continue; // skip session chairs
    let eventTime = program[i][col("Duration/Time")];
    if (eventTime.match("-")) {
      let [start, end] = eventTime.split(/\s*-\s*/);
      log(`Timed event '${item.title}, on: ${item.date}, start: ${start}, end: ${end}`);
      start = start.split(":");
      end = end.split(":");
      let date = item.date.split("/");
      item.start = DateTime.fromObject(
        {
          year: date[2],
          month: date[1],
          day: date[0],
          hour: start[0],
          minute: start[1],
          second: 0
        },
        {zone: timeZoneConference}
      );
      item.end = DateTime.fromObject(
        {
          year: date[2],
          month: date[1],
          day: date[0],
          hour: end[0],
          minute: end[1],
          second: 0
        },
        {zone: timeZoneConference}
      );
      // in the zone chosen in the heading's menu (Live.vue; the program page's, 2026-11-03)
      item.start = item.start.setZone(viewerZone.value);
      item.end = item.end.setZone(viewerZone.value);
    } else {
      let duration = parseInt(program[i][col("Duration/Time")]);
      //log(`Event duration: ${stringify(duration)}`);
      if (!previousEndTime) {
        console.error(`Need a previous event time`);
        continue; // "jumps over" one iteration in the for loop
      }
      item.start = previousEndTime;
      item.end = previousEndTime.plus({minute: duration});
    }
    previousEndTime = item.end; // remember for next event
    item.startTimeStamp = item.start; // remember time stamp for countdown
    item.endTimeStamp = item.end; // remember time stamp for countdown
    log(`startTimeStamp = ${item.startTimeStamp}`);
    // now that times and dates are converted, format them
    item.date = item.start.weekdayLong + ", ";
    item.date += item.start.toFormat("LLL dd");
    item.start = formatTime(item.start);
    item.end = formatTime(item.end);
    item.ended = false; // true => event is over
    //log(`item.end: ${item.end}`);
    output.push(item);
  }
  return getMergedSessions(output);
});

const items = computed(() => {
  log(`items: reSorted = ${reSorted.value}`);
  let output = [];
  if (Array.isArray(test_program.value) && test_program.value.length) {
    log(`Running in test mode: using test program`);
    output = test_program.value;
  } else if (Array.isArray(program.value)) {
    log(`Running in normal mode: using real program`);
    output = program.value;
  } else {
    log(`No program data available`);
    return []; // bail early
  }
  // ⛑️ Ensure output is an array before filtering
  output = (output || []).filter((item) => (props.public ? item.public : item.online));
  if (!reSorted.value) {
    return output;
  }
  log(`check if items need to be (re-)sorted`);
  let [first, second, ...rest] = output;
  log(`live/first = ${stringify(first)}`);
  let i = 1;
  while (first?.ended && i <= output.length) {
    log(`first = ${stringify(first)}`);
    log(`second = ${stringify(second)}`);
    log(`first item has ended; move to end`);
    output = [second, ...rest, first];
    [first, second, ...rest] = output;
    i++;
  }
  return output;
});

// the sessions over in the last day ("Earlier today"), in order; not April's
// the clock the lists follow: a session leaves them as it ends, whether or not its card said so
startServerClock(); // the server's time, not the visitor's clock (serverClock.js)
const now = ref(getServerNow());
useIntervalFn(() => (now.value = getServerNow()), 1000);
const getHasEnded = (item) => item.ended || item.endTimeStamp <= now.value;
const upcoming = computed(() => items.value.filter((item) => !getHasEnded(item)));

// one title size for every card (Seán, 2026-11-03): the longest title, on one line, fills its
// box exactly ("Visual storytelling & Blender programming"), and the others take its size;
// again as the list or the window's width changes
const eventsRef = ref(null);
const {width: windowWidth} = useWindowSize();
// a value compared by content (a new array each second, as the clock ticks, re-ran the fit
// every second: the layout jumped, and Firefox showed its scrollbar each time; 2026-11-03)
const titleSize = useFitLongestLine(eventsRef, ".titleText",
  () => `${upcoming.value.length}|${windowWidth.value}`);
// the event begun: a session of this event (not April's, long past) has started; the feedback
// card shows from then
const hasStarted = computed(() => {
  const recent = now.value.minus({days: 2});
  const isThisEvents = (item) => item.endTimeStamp > recent;
  const begun = items.value.filter(isThisEvents).some((item) => item.startTimeStamp <= now.value);
  return begun || isOver.value;
});
// every session of the event over: the end state, in their place (LiveEndState.vue)
const isOver = computed(() => items.value.length > 0 && !upcoming.value.length);

// the first session after check-in (its first day's second): the dialog opens again for it
// check-in and the first session after it: the event's first day's first two (of the sessions
// not long over: April's aren't this event's). At check-in the button says Check In and the
// dialog waits; it opens for the first session after (Seán, 2026-11-03)
const startStamps = computed(() => {
  const byStart = (first, second) => first.startTimeStamp - second.startTimeStamp;
  const recent = getServerNow().minus({days: 2});
  const sorted = items.value.filter((item) => item.endTimeStamp > recent).sort(byStart);
  const [checkIn, opening] = sorted;
  if (!opening || !checkIn.startTimeStamp.hasSame(opening.startTimeStamp, "day")) return {};
  return {checkIn: getSessionKey(checkIn), opening: getSessionKey(opening)};
});

// each event's program (Seán, 2026-11-03: the Masterclass's, /2026/masterclass#program)
const attendeesPath = computed(() => {
  const kind = props.eventName.startsWith("Masterclass") ? "Masterclass" : "Workshop";
  return `/${year}/People#Online-${kind}-Attendees`;
});
const programPath = computed(() => {
  return props.eventName.startsWith("Masterclass") ? `/${year}/Masterclass#program`
    : `/${year}/Program`;
});

// a card leaving: its height, and the list's gap above it, folded to nothing as it fades
const COLLAPSE_MS = 400;
const isReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
// a card folded away: no height, and the list's gap above it taken back (#events' row gap)
function getFoldedFrame(element) {
  const gap = getComputedStyle(element.parentElement).rowGap;
  return {height: "0px", marginTop: `-${gap}`, opacity: 0, paddingTop: "0px", paddingBottom: "0px"};
}
function collapse(element, done) {
  if (isReducedMotion() || isSwitching.value) return done();
  const animation = element.animate(
    [
      {height: `${element.offsetHeight}px`, marginTop: "0px", opacity: 1},
      getFoldedFrame(element)
    ],
    {duration: COLLAPSE_MS, easing: "ease-in-out"}
  );
  element.style.overflow = "hidden";
  animation.onfinish = done;
}

const eventType = computed(() => (props.eventName.startsWith("Masterclass") ? "Masterclass"
  : "Workshop"));

// the real program's span (not a test's): "18–19 November", in the time zone shown
function getSpan(sessions) {
  const byStart = (first, second) => first.startTimeStamp - second.startTimeStamp;
  const sorted = [...sessions].sort(byStart);
  const ends = sorted.map((item) => item.endTimeStamp);
  return {firstStart: sorted[0].startTimeStamp, lastEnd: DateTime.max(...ends)};
}
const span = computed(() => (getComingAll().length ? getSpan(getComingAll()) : null));
const dateRange = computed(() => {
  if (!span.value) return "";
  const {firstStart, lastEnd} = span.value;
  if (firstStart.hasSame(lastEnd, "day")) return firstStart.toFormat("d LLLL");
  const end = lastEnd.toFormat("d LLLL");
  if (firstStart.hasSame(lastEnd, "month")) return `${firstStart.day}–${end}`;
  return `${firstStart.toFormat("d LLLL")}–${lastEnd.toFormat("d LLLL")}`;
});
// sent once the program has loaded (before, nothing's known: Live.vue keeps the event listed)
watch(() => [span.value, program.value?.length], ([value, count]) => {
  if (!count) return;
  emit("range", {eventName: props.eventName, span: value});
}, {immediate: true});

const earlierToday = computed(() => {
  const dayAgo = getServerNow().minus({days: 1});
  const ended = items.value.filter((item) => getHasEnded(item) && item.endTimeStamp > dayAgo);
  return [...ended].sort((first, second) => first.startTimeStamp - second.startTimeStamp);
});

function formatTime(date) {
  if (!date) {
    return;
  } else if (viewerZone.value.match(/London|Australia|America/)) {
    // use 12 hour clock + AM/PM for these countries
    return date.toFormat("h:mma").toLowerCase();
  } else {
    // use 24 hour clock for all other countries
    return date.toFormat("T").toLowerCase();
  }
}
// the test's pace: each session starts this long after the last one (the first, after the
// click) and lasts as long, so every transition can be previewed quickly
const TEST_STEP_SECONDS = 5;

// a copy of a session, rescheduled for the test: slot 0 starts 5 seconds after the click
function getTestItem(original, slot) {
  const startTimeStamp = getServerNow().plus({seconds: TEST_STEP_SECONDS * (slot + 1)});
  const endTimeStamp = startTimeStamp.plus({seconds: TEST_STEP_SECONDS});
  const date = `${startTimeStamp.weekdayLong}, ${startTimeStamp.toFormat("LLL dd")}`;
  const [start, end] = [formatTime(startTimeStamp), formatTime(endTimeStamp)];
  return {...original, startTimeStamp, endTimeStamp, start, end, date, ended: false};
}

// the sessions still to come (not April's, already over and hidden), each 5 seconds apart
// a copy of a session, as over: they all ended just now, 5 seconds apart, in their order
function getPastItem(original, slot, sessions) {
  const offset = TEST_STEP_SECONDS * (sessions.length - slot + 1);
  return {...getTestItem(original, 0), ...getTimes(getServerNow().minus({seconds: offset}))};
}
function getTimes(startTimeStamp) {
  const endTimeStamp = startTimeStamp.plus({seconds: TEST_STEP_SECONDS});
  const date = `${startTimeStamp.weekdayLong}, ${startTimeStamp.toFormat("LLL dd")}`;
  return {startTimeStamp, endTimeStamp, date, start: formatTime(startTimeStamp),
    end: formatTime(endTimeStamp)};
}

// the real program's sessions shown on /live (online, or public) not long over: the event's
// span (not April's, long past), while it's coming, on, or ended within the last two days
function getComingAll() {
  const isShown = (item) => (props.public ? item.public : item.online);
  const recent = getServerNow().minus({days: 2});
  return (program.value || []).filter((item) => isShown(item) && item.endTimeStamp > recent);
}

// the sessions still to come in the real program (not April's, already over and hidden)
function getComing() {
  const now = getServerNow();
  const isShown = (item) => (props.public ? item.public : item.online);
  return program.value.filter((item) => isShown(item) && item.endTimeStamp > now);
}

// "run": Run Test; "final": Final State; "": what everyone sees
const testMode = ref("");

// Final State: the page as it will be once every session is over
function showFinalState() {
  setSwitching(false);
  const output = getComing().map(getPastItem);
  [reSorted.value, test_program.value, testMode.value] = [0, output, "final"];
  resetLiveMeeting();
}

function run_test() {
  setSwitching(false);
  log(`Test button pressed: calculating test program`);
  const output = getComing().map(getTestItem);
  [reSorted.value, test_program.value, testMode.value] = [0, output, "run"];
  resetLiveMeeting();
  showLinkAt.value = TEST_STEP_SECONDS / 2 / 60; // the waiting room 2.5 seconds before each
}

// Stop Test (Seán, 2026-11-03): back to what everyone sees, the sessions coming back down into
// view (the test's leave at once): the reverse of a session ending, for the admin's eyes only
const isRestoring = ref(false);
// Run Test or Stop Test swapping the lists: the sessions leaving go at once, nothing folds
// (Seán, 2026-11-03: starting a test moves nothing)
const isSwitching = ref(false);
function setSwitching(isRestore) {
  [isSwitching.value, isRestoring.value] = [true, isRestore];
  setTimeout(() => ([isSwitching.value, isRestoring.value] = [false, false]), COLLAPSE_MS + 100);
}
function expand(element, done) {
  if (isReducedMotion() || !isRestoring.value) return done();
  const animation = element.animate(
    [
      getFoldedFrame(element),
      {height: `${element.offsetHeight}px`, marginTop: "0px", opacity: 1}
    ],
    {duration: COLLAPSE_MS, easing: "ease-in-out"}
  );
  element.style.overflow = "hidden";
  animation.onfinish = () => {
    element.style.overflow = "";
    done();
  };
}

function stopTest() {
  setSwitching(true);
  reset();
}

function reset() {
  showLinkAt.value = 15; // what everyone sees: the link 15 minutes before
  // reset sort counter and turn off test
  [reSorted.value, test_program.value, testMode.value] = [0, [], ""];
  resetLiveMeeting();
}
function eventHasEnded(timeStamp) {
  const activeProgram = test_program.value.length ? test_program.value : program.value;

  for (let item of activeProgram) {
    if (item.startTimeStamp.toMillis() === timeStamp?.toMillis?.()) { // the moment, any zone
      item.ended = true;
      reSorted.value++;
      return;
    }
  }
  console.error(`Live/eventHasEnded: couldn't find matching item`);
}

onMounted(async () => {
  log(`onMounted()`);
  try {
    programOriginal.value = (await fetchProgramSheet(year)).program || [];
  } catch (error) {
    console.error(`Could not load program file: ${error}`);
    // alert(`Could not load program file: ${error}`); // Uncomment if needed
  }
});

// watch(
//   () => store.state.TimeZone,
//   (newTimeZone) => (timeZone.value = newTimeZone)
// );

// watch(
//   () => store.state.TimeZone,
//   (newTimeZone) => {
//     log(`newTimeZone = ${newTimeZone}`);
//     timeZone.value = newTimeZone;
//   },
//   {deep: true, immediate: true}
// );
</script>

<style scoped>
h3 {
  display: inline;
  float: left;
}
.zoom-container {
  /* Centering the button if needed */
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.zoom-button {
  background-color: #2d8cff; /* Zoom's blue color */
  color: white; /* White text */
  border: none; /* Remove default border */
  padding: 10px 20px; /* Padding inside the button */
  font-size: 16px; /* Text size */
  cursor: pointer; /* Pointer cursor on hover */
  border-radius: 5px; /* Slightly rounded corners */
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2); /* Optional: adds a slight shadow for depth */
  transition: background-color 0.3s ease; /* Smooth transition for hover effect */
}

#timezone {
  float: right;
  margin-top: 12px; /* same as App.vue/h3 */
  margin-bottom: 8px; /* same as App.vue/h3 */
  font-size: 14px;
  line-height: 33px;
  clear: right; /* https://stackoverflow.com/q/3415635 */
}
#signin {
  clear: both; /* needed to clear two floats above */
  margin-top: 5px;
}
#events {
  --title-wrap: nowrap; /* each title on one line, the longest filling its box */
  margin-top: 0px;
  margin-bottom: var(--live-section-gap, 24px); /* to the feedback card, when it follows */
  display: flex;
  flex-direction: column;
  gap: var(--live-card-gap, 16px);
}
/* the gaps: Live.vue's --live-section-gap and --live-card-gap */
.earlierToday {
  margin: 0 0 var(--live-section-gap, 24px);
}
/* the event coming up's row: its dates smaller and lighter, under the event's name */
.rowTitle,
.rowDate {
  display: block;
}
.rowDate {
  font-size: 0.85rem;
  font-weight: 400;
  color: color-mix(in srgb, var(--text-color) 65%, transparent);
}
.comingUp {
  margin-top: var(--live-section-gap, 24px);
}
.quietButtons {
  display: inline-grid; /* the columns all as wide as the widest label */
  max-width: 100%; /* never wider than the screen (a phone, with the test buttons: 2026-11-03) */
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr); /* equal, and able to shrink: a label wraps */
  gap: 8px;
  margin-bottom: var(--live-section-gap, 24px);
}
.quietButton {
  box-sizing: border-box;
  text-align: center;
  margin: 0; /* global.css's button look reset (CLAUDE.md): this one is an outline */
  box-shadow: none;
  background: none;
  font: inherit;
  cursor: pointer;
  padding: 0.35em 0.9em;
  border: 1.5px solid
    color-mix(in srgb, white var(--second-text-lift), var(--brand-color));
  border-radius: 8px;
  color: color-mix(
    in srgb,
    white var(--second-text-lift),
    color-mix(in srgb, black var(--second-text-darken), var(--brand-color))
  );
  font-weight: 700;
  text-decoration: none;
}
.quietButton:hover {
  background: color-mix(in srgb, var(--brand-color) 12%, transparent);
  text-decoration: none;
}
/* an action button (Seán, 2026-11-03): the brand's full strength, white on green, "you should
   press this" (DESIGN.md § 5); Stop Test, while a test runs; the same size as the quiet ones */
.quietButton.actionButton {
  border-color: var(--brand-dark-color);
  background: var(--brand-dark-color);
  color: white;
}
.quietButton.actionButton:hover {
  background: var(--brand-dark-color);
  filter: brightness(1.15);
}
/* Earlier today appearing, as the first session ends */
.earlier-enter-active {
  transition: opacity 400ms ease-out;
}
.earlier-enter-from {
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .earlier-enter-active {
    transition: none;
  }
}
p.intro {
  font-weight: 300;
  font-style: italic;
}
div.description {
  text-align: left;
  text-decoration: none;
  font-size: 1.1rem;
  color: black;
}
h3 span {
  font-weight: 300;
}
</style>
